/**
 * Share page for the PWA Web Share Target.
 *
 * The gateway redirects shared items here as "#/share?title=...&text=...&url=...
 * &file=...". This page sends the shared item as a message to the currently
 * open session (or a new session if none is open), then navigates to that
 * session page. The gateway builds the correct parts: text for text/plain-type
 * shares, images for image/*, and file parts for anything else.
 */
import { useEffect, useMemo } from 'preact/hooks';
import { getOpenSession } from '../lib/share.js';
import { createSession, sendMessage } from './Session.jsx';

function parseShareFromHash(hash) {
  // hash looks like "#/share?title=..&text=..&url=..&file=a&file=b"
  const qi = hash.indexOf('?');
  if (qi < 0) return null;
  const params = new URLSearchParams(hash.slice(qi + 1));
  const files = params.getAll('file').filter(Boolean);
  const text = params.get('text') || '';
  const title = params.get('title') || '';
  const url = params.get('url') || '';
  if (!files.length && !text && !url && !title) return null;
  return { files, text, title, url };
}

export function SharePage() {
  const share = useMemo(() => parseShareFromHash(window.location.hash), []);

  useEffect(() => {
    const run = async () => {
      if (!share) {
        window.location.hash = '#/';
        return;
      }

      // The message text: prefer the shared text, then the URL/title.
      const message = share.text || share.url || share.title;

      const openID = getOpenSession();
      if (openID) {
        await sendMessage(openID, message, share.files);
        window.location.replace(`#/s/${openID}`);
      } else {
        const sess = await createSession(message || 'Shared item', message, share.files);
        window.location.replace(sess && sess.id ? `#/s/${sess.id}` : '#/');
      }
    };

    // Replace the hash so the share query is not reprocessed on refresh.
    try {
      history.replaceState(null, '', `${window.location.pathname}${window.location.search}#/`);
    } catch {
      /* ignore */
    }
    run();
  }, [share]);

  return (
    <div className="flex-1 flex items-center justify-center px-4">
      <div className="flex items-center gap-3 text-base-content/50">
        <span className="loading loading-spinner loading-sm" />
        <span className="text-sm">Sharing to session…</span>
      </div>
    </div>
  );
}